<script setup lang="ts">
  import { computed } from 'vue'
  import { isStakeholderClient, testIds, type Client } from '@gat-ship/types'
  import { text } from '@gat-ship/stakeholders'
  import { IconLoading } from '@gat-ship/icons'
  import {
    ModalSection,
    ModalSectionHeader,
    ModalSectionHeaderTitle,
    LinkEmail,
    Button
  } from '@gat-ship/vue-components'
  import { useStakeholderContext } from '../../../composables/useStakeholder'
  import { useStakeholderModals } from '../../providers/stakeholder-modals'
  import {
    useStakeholderEditAction,
    type StakeholderEditButtonConfig
  } from '../../../composables/useStakeholderEditAction'

  const service = useStakeholderContext()
  const modals = useStakeholderModals()

  const client = computed<Client | undefined>(() => {
    const value = service?.stakeholder.value
    return value && isStakeholderClient(value) ? value : undefined
  })

  const { isLoading, canShow, btnConfig } = useStakeholderEditAction(
    () => client.value,
    () =>
      text.groups.billingDetails(client.value?.stakeholderType ?? 'client')
        .buttons.updateBillingDetails as StakeholderEditButtonConfig
  )
</script>

<template>
  <ModalSection v-if="client">
    <ModalSectionHeader>
      <ModalSectionHeaderTitle>
        {{ text.groups.billingDetails(client.stakeholderType).title }}
      </ModalSectionHeaderTitle>
    </ModalSectionHeader>
    <div class="divide-y">
      <div class="flex items-center justify-between gap-4 py-3">
        <span class="text-sm font-medium">Invoice Email</span>
        <LinkEmail
          :email="client.invoiceEmail ?? undefined"
          :class="{ 'text-muted-foreground': !client.invoiceEmail }"
          no-email-text="No invoice email."
        />
      </div>
      <div class="flex items-center justify-between gap-4 py-3">
        <span class="text-sm font-medium">Invoice Currency</span>
        <span class="text-muted-foreground text-sm">
          <template v-if="client.useDefaultBillingCurrency">
            Default invoice currency
          </template>
          <template v-else>{{ client.billingCurrency?.code }}</template>
        </span>
      </div>
      <div class="flex items-center justify-between gap-4 py-3">
        <span class="text-sm font-medium">Payment Terms</span>
        <span class="text-muted-foreground text-sm">
          <template v-if="client.useDefaultPaymentTerms">
            Default payment terms
          </template>
          <template v-else>
            <span v-if="client.paymentTermsUseRunningMonth"
              >End of month +
            </span>
            <span>{{ client.paymentTermsPeriodInDays }} days</span>
          </template>
        </span>
      </div>
    </div>
    <Button
      v-if="canShow"
      :variant="btnConfig.variant"
      :size="btnConfig.size"
      :class="btnConfig.class"
      :disabled="isLoading"
      :data-testid="testIds.stakeholders.actions.updateBillingInformation"
      @click="modals?.showUpdateBillingDetails({ stakeholder: client })"
    >
      <IconLoading v-if="isLoading" class="size-4 shrink-0" />
      <component
        :is="btnConfig.icon"
        v-else-if="btnConfig.icon"
        class="size-4"
      />
      {{ btnConfig.label }}
    </Button>
  </ModalSection>
</template>
